import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { CaretRight, Sparkle, CheckCircle } from '@phosphor-icons/react';
import { useCompanyFunctions } from '@/contexts/CompanyFunctionsContext';

/**
 * Mijn Dag widget — actiegerichte top-widget op Dashboard.
 * Toont vervallen facturen, klaar-voor-herinnering, vervalt-binnen-7-dagen,
 * lage voorraad, oude concepten en offertes zonder antwoord.
 */
export default function MijnDagWidget() {
  const { isVisible } = useCompanyFunctions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard/mijn-dag')
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mb-6 bg-white rounded-2xl p-5 animate-pulse" style={{ border: '1px solid rgba(0,0,0,0.06)' }}>
        <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
        <div className="h-3 w-full bg-gray-100 rounded" />
      </div>
    );
  }
  if (!data) return null;
  const visibleTasks = (data.taken || []).filter(t => isVisible(t.actie_url));
  if (data.aantal_taken > 0 && !visibleTasks.length) return null;

  const prioKleur = {
    kritiek: { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' },
    hoog: { bg: '#FED7AA', border: '#EA580C', text: '#9A3412' },
    middel: { bg: '#FEF3C7', border: '#D97706', text: '#92400E' },
    laag: { bg: '#F3F4F6', border: '#6B7280', text: '#374151' },
  };

  // Alle rustige staat — geen acties vandaag
  if (data.aantal_taken === 0) {
    return (
      <div
        className="mb-6 bg-gradient-to-br from-green-50 via-emerald-50 to-white rounded-2xl p-5 flex items-center gap-4"
        style={{ border: '1px solid rgba(22,163,74,0.15)' }}
        data-testid="mijn-dag-widget"
      >
        <div
          className="flex items-center justify-center shrink-0"
          style={{ width: 56, height: 56, borderRadius: '50%', background: '#16A34A', boxShadow: '0 0 20px rgba(22,163,74,0.3)' }}
        >
          <CheckCircle size={28} weight="bold" color="#fff" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-bold text-gray-900">Alles onder controle ✨</div>
          <div className="text-[12px] text-gray-600">Geen dringende taken vandaag — ga iets leuks doen!</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="mb-6 bg-white rounded-2xl overflow-hidden"
      style={{ border: '1px solid rgba(0,0,0,0.06)' }}
      data-testid="mijn-dag-widget"
    >
      {/* Header */}
      <div className="px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Sparkle size={20} weight="duotone" className="text-indigo-600" />
          <div>
            <div className="text-[13px] font-bold text-gray-900">Mijn dag</div>
            <div className="text-[11px] text-gray-500">
              {visibleTasks.length} tak{visibleTasks.length === 1 ? '' : 'en'}
              {isVisible('/facturen') && data.openstaand_totaal > 0 && <> · <strong>{fmt(data.openstaand_totaal)}</strong> openstaand</>}
            </div>
          </div>
        </div>
        <div className="text-[10px] text-gray-400 font-mono">{data.datum}</div>
      </div>

      {/* Taken-lijst */}
      <div className="divide-y divide-gray-100">
        {visibleTasks.map((t, i) => {
          const c = prioKleur[t.prio] || prioKleur.laag;
          return (
            <button
              key={i}
              onClick={() => navigate(t.actie_url)}
              className="w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 active:bg-gray-100 transition-colors"
              data-testid={`mijn-dag-taak-${i}`}
            >
              <div
                className="flex items-center justify-center shrink-0 text-lg"
                style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: c.bg, border: `1px solid ${c.border}30`,
                }}
              >
                {t.icoon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-bold text-gray-900 truncate">{t.titel}</div>
                <div className="text-[11px] text-gray-500 truncate">{t.detail}</div>
              </div>
              <span
                className="text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0"
                style={{ background: `${c.border}15`, color: c.text, border: `1px solid ${c.border}30` }}
              >
                {t.actie_label}
              </span>
              <CaretRight size={14} weight="bold" className="text-gray-300" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
